import { cn } from "@/lib/utils";

export type ProgressPieProps = {
  /** Completion from 0–100. Clamped. */
  value: number;
  /** Rendered size in px. Defaults to 22 to match the design spec. */
  size?: number;
  className?: string;
};

const RADIUS = 9;
const CENTER = 12;

/**
 * Determinate pie progress indicator — a filled wedge that grows clockwise from
 * the top, ringed by the remaining track. Mirrors Material's `clock_loader`
 * used in the design. Color follows `currentColor`, so set text color to theme it.
 */
export default function ProgressPie({
  value,
  size = 22,
  className,
}: ProgressPieProps) {
  const pct = Math.max(0, Math.min(100, value));
  const angle = (pct / 100) * 360;
  const radians = ((angle - 90) * Math.PI) / 180;
  const endX = CENTER + RADIUS * Math.cos(radians);
  const endY = CENTER + RADIUS * Math.sin(radians);
  const largeArc = angle > 180 ? 1 : 0;
  const wedge = `M ${CENTER} ${CENTER} L ${CENTER} ${CENTER - RADIUS} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${endX} ${endY} Z`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={cn("flex-shrink-0", className)}
      aria-hidden="true"
    >
      <circle
        cx={CENTER}
        cy={CENTER}
        r={RADIUS}
        stroke="currentColor"
        strokeWidth={2}
      />
      {pct >= 100 ? (
        <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="currentColor" />
      ) : (
        pct > 0 && <path d={wedge} fill="currentColor" />
      )}
    </svg>
  );
}
